import React, { useState, useEffect, useRef } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { useTheme as useCustomTheme } from '../context/ThemeContext';

const MovingTarget = () => {
  const theme = useTheme();
  const { activeThemeDefinition } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  // Tailles responsives
  const getViseurSize = () => {
    if (isMobile) return 70;
    if (isTablet) return 90;
    return 120; // Desktop
  };
  
  const getImpactSize = () => {
    if (isMobile) return 35;
    if (isTablet) return 45;
    return 60; // Desktop
  };
  
  const viseurSize = getViseurSize();
  const impactSize = getImpactSize();
  
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [impacts, setImpacts] = useState<Array<{id: number; x: number; y: number; rotation: number; image: string; removing?: boolean;}>>([]);
  const animationRef = useRef<number | null>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const idRef = useRef<number>(0);
  const [transitionTime, setTransitionTime] = useState<number>(0.5);
  
  // Function to add an impact at the current position
  const addImpact = (x: number, y: number) => {
    // Choisir une image d'impact aléatoire et ajouter avec un id
    // Utiliser les impacts du thème si disponibles
    const themeImpacts = activeThemeDefinition?.assets?.impacts?.map(i => i.url) || [];
    
    if (themeImpacts.length === 0) return; // Pas d'impacts si le thème n'en définit pas
    
    const randomImage = themeImpacts[Math.floor(Math.random()*themeImpacts.length)];
    const randomRotation = Math.floor(Math.random()*360);
    setImpacts(prev => {
      const newId = idRef.current++;
      const newImpact = { id: newId, x, y, rotation: randomRotation, image: randomImage, removing: false };
      const newImpacts = [...prev, newImpact];
      if (newImpacts.length > 15) {
        const toRemoveCount = newImpacts.length - 15;
        for (let i = 0; i < toRemoveCount; i++) newImpacts[i].removing = true;
        setTimeout(() => {
          setImpacts(current => current.filter(imp => !imp.removing));
        }, 2000);
      }
      return newImpacts;
    });
  };
  
  useEffect(() => {
    // Function to move the target randomly
    const moveTarget = () => {
      if (!targetRef.current || !containerRef.current) return;
      
      const container = containerRef.current.getBoundingClientRect();
      const target = targetRef.current.getBoundingClientRect();
      
      // Calculate maximum positions
      const maxX = container.width - target.width;
      const maxY = container.height - target.height;
      
      // Generate random position with emphasis on vertical movement
      // Use full width range
      const newX = Math.random() * maxX;
      // Force more vertical movement by using the full height range
      // and occasionally going beyond the normal bounds
      const verticalEmphasis = Math.random() > 0.5 ? 1.5 : 1;
      const newY = Math.min(maxY, Math.random() * maxY * verticalEmphasis);
      
      // Déplacer le viseur (l’impact sera ajouté après la transition CSS)
      setPosition({ x: newX, y: newY });
      
      // Générer une durée de transition aléatoire
      const randomTransition = 0.3 + Math.random() * 1;
      setTransitionTime(randomTransition);
      
      // Définir un délai de déplacement aléatoire
      const randomDelay = 1000 + Math.random() * 4000;
      
      // Schedule next movement
      animationRef.current = window.setTimeout(moveTarget, randomDelay);
    };
    
    // Start the animation
    moveTarget();
    
    // Cleanup
    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, []);
  
  return (
    <Box 
      ref={containerRef}
      sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%',
        zIndex: 2,
        pointerEvents: 'none'
      }}
    >
      {/* Display impact images */}
      {impacts.map((impact) => (
        <Box
          key={impact.id}
          sx={{
            position: 'absolute',
            width: `${impactSize}px`,
            height: `${impactSize}px`,
            left: `${impact.x - impactSize/2}px`,  // Center the impact
            top: `${impact.y - impactSize/2}px`,   // Center the impact
            pointerEvents: 'none',
            zIndex: 1,
            opacity: impact.removing ? 0 : 1,
            transition: 'opacity 2s ease'
          }}
        >
          <img 
            src={impact.image} 
            alt="Impact" 
            style={{
              width: '100%',
              height: '100%',
              transform: `rotate(${impact.rotation}deg)`,
              opacity: 0.85
            }}
          />
        </Box>
      ))}
      
      {/* Display the viseur */}
      {activeThemeDefinition?.assets?.cursorEnabled && activeThemeDefinition?.assets?.cursor?.url && (
        <Box
            ref={targetRef}
            onTransitionEnd={(e) => {
            if ((e as React.TransitionEvent<HTMLDivElement>).propertyName === 'left') {
                const rect = targetRef.current?.getBoundingClientRect();
                if (rect) {
                // Désactiver les impacts si le thème le demande (optionnel, ici on suppose toujours actifs)
                addImpact(position.x + rect.width / 2, position.y + rect.height / 2);
                }
            }
            }}
            sx={{
            position: 'absolute',
            width: `${viseurSize}px`,
            height: `${viseurSize}px`,
            left: `${position.x}px`,
            top: `${position.y}px`,
            transition: `all ${transitionTime}s ease-out`,
            pointerEvents: 'none',
            zIndex: 3,
            }}
        >
            <img 
            src={activeThemeDefinition.assets.cursor.url} 
            alt="Viseur" 
            style={{
                width: '100%',
                height: '100%'
            }}
            />
        </Box>
      )}
    </Box>
  );
};

export default MovingTarget;
